import React from 'react';
import theme from 'themes/volia';
import styled from 'styled-components/macro';
import { findIndex } from 'lodash';
import scaleSize from 'utils/scale-size';
import getImagePath from 'utils/get-image-path';
import calculateProgress from 'utils/calculate-progress';
import VerticalList from 'components/VerticalList';
import Progress from 'components/Progress';
import { ReactComponent as CatchUpIcon } from 'assets/img/catch-up-icon.svg';
import { StyledEpgItemWrapper, StyledChannelItem } from './StyledComponents';

const CONFIG = {
  enterTo: 'default-element',
  defaultElement: '.selected',
  leaveFor: {
    up: '',
    down: '',
  },
};

export const StyledListWrapper = styled.div`
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  display: inline-block;
`;

export const StyledChannelsList = styled(VerticalList)`
  width: ${scaleSize(910)}px;
  height: ${scaleSize(1080)}px;
  margin-top: ${scaleSize(13)}px;
  overflow: hidden;
  display: inline-block;
  vertical-align: top;
`;

export const StyledListTitle = styled.div`
  margin-left: ${scaleSize(20)}px;
  height: ${scaleSize(48)}px;
  width: ${scaleSize(800)}px;
  font-style: normal;
  font-weight: 500;
  font-size: ${scaleSize(34)}px;
  line-height: ${scaleSize(48)}px;
  color: ${({ theme }) => theme.defaultColor};
  padding-bottom: ${scaleSize(20)}px;
  border-bottom: 1px solid ${({ theme }) => theme.defaultColor};
  letter-spacing: -0.277778px;
  text-transform: uppercase;
`;

export const StyledChannelItemLogo = styled.div.attrs(({ url }) => ({
  style: { backgroundImage: `url(${getImagePath(url, scaleSize(160), scaleSize(160))})` },
}))`
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  width: ${scaleSize(140)}px;
  height: ${scaleSize(140)}px;
  display: inline-block;
  margin: ${scaleSize(10)}px ${scaleSize(40)}px 0 ${scaleSize(20)}px;
  vertical-align: top;
`;

export const StyledChannelItemDescription = styled.div`
  width: ${scaleSize(590)}px;
  height: ${scaleSize(160)}px;
  display: inline-block;
  padding: ${scaleSize(20)}px 0;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;

  & > div {
    white-space: nowrap;
    overflow: hidden;
    display: block;
    text-overflow: ellipsis;
    color: ${({ isActive, theme }) => (isActive ? theme.activeColor : theme.defaultColor)};
    font-size: ${scaleSize(40)}px;
    line-height: ${scaleSize(48)}px;

    & > span {
      float: left;
      max-width: ${scaleSize(540)}px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    & > svg {
      margin-left: ${scaleSize(5)}px;
      margin-top: ${scaleSize(3)}px;
      width: ${scaleSize(40)}px;
      height: ${scaleSize(40)}px;
      float: left;
    }
  }

  & > i {
    white-space: nowrap;
    display: block;
    font-style: normal;
    color: ${({ theme }) => theme.defaultColor};
    font-size: ${scaleSize(34)}px;
    margin-top: ${scaleSize(8)}px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const StyledChannelItemProgress = styled(Progress)`
  left: 0;
  bottom: ${scaleSize(20)}px;
`;

const ChannelItem = ({ id, onEnter, onFocus, item, isActive, isCurrentlyPlaying, index, width }) => {
  const { imageUrl, title, notAvailable, currentProgram, isCatchUpAvailable, isVod } = item;
  const isRecorded = isCatchUpAvailable || isVod;

  return (
    <StyledEpgItemWrapper
      id={id}
      onEnter={() => onEnter(index)}
      onFocus={onFocus}
      notAvailable={notAvailable}
      index={index}
      width={width}
      type={'channel'}
      className={isActive ? 'selected' : ''}
    >
      <StyledChannelItem>
        <StyledChannelItemLogo url={imageUrl} />
        <StyledChannelItemDescription isActive={isCurrentlyPlaying}>
          <div>
            <span>{title}</span>
            {isRecorded && <CatchUpIcon />}
          </div>
          {currentProgram && (
            <>
              <i>{currentProgram.title}</i>
              <StyledChannelItemProgress
                progress={calculateProgress(currentProgram.startTime, currentProgram.endTime)}
                background={theme.submenuActiveColor}
              />
            </>
          )}
        </StyledChannelItemDescription>
      </StyledChannelItem>
    </StyledEpgItemWrapper>
  );
};

export const ChannelsList = (props) => {
  const {
    title,
    content = [],
    focusedChannelId,
    currentPlayingChannelId,
    onListFocus,
    renderSize,
    width,
    isVisible,
  } = props;
  const { sectionName, onFocus, onEnter } = props;

  const defaultIndex = findIndex(content, { id: focusedChannelId });

  return (
    <StyledListWrapper isVisible={isVisible}>
      <StyledListTitle>{title}</StyledListTitle>
      <StyledChannelsList
        listId={'channels-list-wrapper'}
        sectionName={sectionName}
        items={content}
        itemRender={(props) => (
          <ChannelItem {...props} isCurrentlyPlaying={props.item.id === currentPlayingChannelId} width={width} />
        )}
        defaultIndex={defaultIndex >= 0 ? defaultIndex : 0}
        itemHeight={180}
        width={width}
        onFocus={onFocus}
        onEnter={onEnter}
        onListFocus={onListFocus}
        config={CONFIG}
        renderSize={renderSize}
        infinite
      />
    </StyledListWrapper>
  );
};

export default ChannelsList;
